if (!customElements.get('collection-description')) {
  class CollectionDescription extends HTMLElement {
    constructor() {
      super();
      this.descriptionContent = this.querySelector('[data-description-content]');
      this.toggleButton = this.querySelector('[data-description-toggle]');
      this.toggleTextMore = this.querySelector('[data-toggle-text-more]');
      this.toggleTextLess = this.querySelector('[data-toggle-text-less]');
      this.isExpanded = false;
      this.maxHeight = 50; // Match CSS max-height

      this.init();
      this.addListeners();
    }

    init() {
      // Check if content needs truncation
      this.checkIfToggleNeeded();
    }

    addListeners() {
      if (this.toggleButton) {
        this.toggleButton.addEventListener('click', this.handleToggle.bind(this));
      }

      // Recheck on window resize
      this.resizeHandler = this.resizeHandler || this.handleResize.bind(this);
      window.addEventListener('resize', this.resizeHandler);
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.resizeHandler);
    }

    /**
     * Checks if the content height exceeds the max-height threshold
     */
    checkIfToggleNeeded() {
      if (!this.descriptionContent) return;

      // Temporarily remove max-height to measure full content
      const originalMaxHeight = this.descriptionContent.style.maxHeight;
      const originalClass = this.classList.contains('is-expanded');
      
      this.descriptionContent.style.maxHeight = 'none';
      if (originalClass) {
        this.classList.remove('is-expanded');
      }

      const contentHeight = this.descriptionContent.scrollHeight;

      // Restore original state
      this.descriptionContent.style.maxHeight = originalMaxHeight;
      if (originalClass) {
        this.classList.add('is-expanded');
      }

      // Show toggle button only if content exceeds max-height
      if (contentHeight > this.maxHeight) {
        this.classList.add('needs-toggle');
        if (!this.isExpanded) {
          this.descriptionContent.style.maxHeight = `${this.maxHeight}px`;
        }
      } else {
        this.classList.remove('needs-toggle');
        this.descriptionContent.style.maxHeight = '';
      }
    }

    /**
     * Handles window resize events
     */
    handleResize() {
      // Debounce resize check
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => {
        this.checkIfToggleNeeded();
      }, 250);
    }

    /**
     * Handles toggle button click
     */
    handleToggle() {
      this.isExpanded = !this.isExpanded;

      if (this.isExpanded) {
        this.expand();
      } else {
        this.collapse();
      }

      // Update ARIA attributes
      this.toggleButton.setAttribute('aria-expanded', this.isExpanded.toString());
      
      // Update button text visibility
      if (this.toggleTextMore && this.toggleTextLess) {
        this.toggleTextMore.style.display = this.isExpanded ? 'none' : 'inline';
        this.toggleTextLess.style.display = this.isExpanded ? 'inline' : 'none';
      }
    }

    /**
     * Expands the description content
     */
    expand() {
      this.classList.add('is-expanded');
      // Set to actual height for smooth transition
      this.descriptionContent.style.maxHeight = `${this.descriptionContent.scrollHeight}px`;
      
      // After transition, set to none for dynamic content
      setTimeout(() => {
        if (this.isExpanded) {
          this.descriptionContent.style.maxHeight = 'none';
        }
      }, 300);
    }

    /**
     * Collapses the description content
     */
    collapse() {
      // Set to current height first for smooth transition
      this.descriptionContent.style.maxHeight = `${this.descriptionContent.scrollHeight}px`;
      
      // Force reflow
      this.descriptionContent.offsetHeight;
      
      this.classList.remove('is-expanded');
      this.descriptionContent.style.maxHeight = `${this.maxHeight}px`;
    }
  }

  customElements.define('collection-description', CollectionDescription);
}

